"use client";

// "Our journey" — horizontal milestone carousel used on /about. Chronological
// left → right; starts scrolled to the END so the current (latest) year is
// visible by default. Left/right arrows match the Testimonials scroller;
// native swipe works on touch. Same admin-managed data as the vertical
// timeline on /investor-relations-v2.
import { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "@/components/Container";
import { MilestonesTimeline, MsfBadge, splitMsf } from "@/components/sections/MilestonesTimeline";

type Milestone = { year: string; points: string[] };

export function MilestonesCarousel({ milestones }: { milestones: Milestone[] }) {
  // Milestones are stored oldest first, which is the reading order for a
  // horizontal timeline. The carousel opens scrolled fully right, so the
  // newest year is what you see first.
  const ordered = milestones;

  const scroller = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    // Jump to the end so the latest year is in view on load.
    el.scrollLeft = el.scrollWidth;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update, ordered.length]);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    const card = el.querySelector("article");
    const step = card ? card.getBoundingClientRect().width + 20 /* gap-5 */ : el.clientWidth;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const arrowCls =
    "flex h-11 w-11 items-center justify-center rounded-full border border-brand-line text-brand-ink transition-colors hover:border-brand-accent hover:text-brand-accent disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-brand-line disabled:hover:text-brand-ink";

  return (
    <div>
      {/* Mobile — vertical icon-rail timeline, same as the Investor
          Relations page's Our journey */}
      <Container className="sm:hidden">
        <MilestonesTimeline milestones={milestones} />
      </Container>

      {/* sm+ — full-bleed horizontal carousel with arrows */}
      <div className="hidden sm:block">
      {/* arrows — aligned with the page container; the scroller below runs
          edge-to-edge across the full page width */}
      <Container className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => scrollByCard(-1)}
          disabled={!canLeft}
          aria-label="Earlier milestones"
          className={arrowCls}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => scrollByCard(1)}
          disabled={!canRight}
          aria-label="Later milestones"
          className={arrowCls}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </Container>

      {/* scroller — full-bleed, edge to edge of the page */}
      <div
        ref={scroller}
        onScroll={update}
        className="mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto px-6 pb-4 md:px-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {ordered.map((m, idx) => {
          const { rest, msf, msfNote } = splitMsf(m.points);
          const progress = ordered.length > 1 ? idx / (ordered.length - 1) : 1;
          return (
            <article
              key={m.year}
              className="flex w-[85%] shrink-0 snap-start flex-col rounded-2xl border border-brand-line bg-brand-surface p-6 sm:w-[380px]"
            >
              <div className="flex items-center gap-3">
                <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full bg-brand-accent" />
                <span className="text-2xl font-semibold tracking-tight text-brand-accent">{m.year}</span>
              </div>
              <ul className="mt-5 space-y-2">
                {rest.map((pt, i) => (
                  <li key={i} className="flex gap-2.5 text-sm font-normal leading-relaxed text-brand-slate">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand-accent" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
              {msf && (
                // mt-auto pins the badge row to the card bottom so the bubbles
                // line up across the carousel like the slide's growth curve
                <div className="mt-auto pt-5">
                  <div className="border-t border-brand-line pt-4">
                    <MsfBadge value={msf} progress={progress} note={msfNote} />
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
      </div>
    </div>
  );
}
